/**
 * Home page component — Author landing/navigation page.
 *
 * Features:
 * - Welcome message with universal semantic search
 * - Quick stats (essays, notes, comments, suggestions)
 * - Grid of navigation actions
 * - Mobile-friendly responsive layout
 *
 * Called by:
 * - App.tsx as route for /reviewer
 */
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spin } from 'antd';
import {
  BookOutlined,
  FileTextOutlined,
  CommentOutlined,
  BulbOutlined,
  BarChartOutlined,
  SettingOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { getEssays, getNotes, getAdminStats, semanticSearch } from '../services/api';
import type { SemanticSearchResult } from '../services/api';

function HomePage() {
  const navigate = useNavigate();
  const { user, isAuthor } = useAuth();

  const { data: essays = [] } = useQuery({
    queryKey: ['essays'],
    queryFn: () => getEssays(),
  });

  const { data: notes = [] } = useQuery({
    queryKey: ['notes'],
    queryFn: () => getNotes(),
    enabled: isAuthor,
  });

  const { data: stats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => getAdminStats(),
    enabled: isAuthor,
  });

  const statCards = [
    { label: 'Essays', value: essays.length, icon: <BookOutlined /> },
    { label: 'Notes', value: notes.length, icon: <FileTextOutlined /> },
    { label: 'Comments', value: stats?.totalComments ?? '—', icon: <CommentOutlined /> },
    { label: 'Suggestions', value: stats?.totalSuggestions ?? '—', icon: <BulbOutlined /> },
  ];

  const actions = [
    { label: 'Essays', description: 'Browse & edit essays', icon: <BookOutlined />, path: '/reviewer/essays' },
    { label: 'Notes', description: 'Manage your notes', icon: <FileTextOutlined />, path: '/reviewer/notes', authorOnly: true },
    { label: 'Analytics', description: 'Traffic & engagement', icon: <BarChartOutlined />, path: '/reviewer/analytics', authorOnly: true },
    { label: 'Admin', description: 'Users & moderation', icon: <SettingOutlined />, path: '/reviewer/admin', authorOnly: true },
    { label: 'Search', description: 'Keyword search', icon: <SearchOutlined />, path: '/reviewer/search' },
    { label: 'Series', description: 'Manage essay series', icon: <UnorderedListOutlined />, path: '/reviewer/essays' },
  ];

  const visibleActions = actions.filter((a) => !a.authorOnly || isAuthor);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
      {/* Welcome */}
      <h1
        style={{
          fontFamily: "var(--font-serif)",
          fontWeight: 400,
          fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
          color: '#f5f5f5',
          textAlign: 'center',
          margin: '0 0 20px',
        }}
      >
        Welcome back, {user?.name?.split(' ')[0]}
      </h1>

      {/* Semantic search bar */}
      <SemanticSearchBar />

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 10,
          marginBottom: 36,
        }}
      >
        {statCards.map((s) => (
          <StatCard key={s.label} label={s.label} value={s.value} icon={s.icon} />
        ))}
      </div>

      {/* Actions grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 10,
        }}
      >
        {visibleActions.map((a) => (
          <ActionCard
            key={a.label}
            label={a.label}
            description={a.description}
            icon={a.icon}
            onClick={() => navigate(a.path)}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Semantic Search Bar
// =============================================================================

function SemanticSearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<SemanticSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce: wait 600ms after typing stops before searching
  useEffect(() => {
    if (query.trim().length < 3) {
      setDebouncedQuery('');
      setResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 600);
    return () => clearTimeout(timer);
  }, [query]);

  // Run semantic search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery) return;
    let cancelled = false;
    setIsSearching(true);
    setSelectedIndex(-1);

    semanticSearch(debouncedQuery, 8)
      .then((response) => {
        if (!cancelled) {
          setResults(response.results);
          setShowResults(true);
          setIsSearching(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setIsSearching(false);
        }
      });

    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // Close results on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((result: SemanticSearchResult) => {
    if (result.type === 'essay') {
      navigate(`/reviewer/essays/${result.slug}`);
    } else {
      navigate(`/reviewer/notes/${result.id}`);
    }
    setQuery('');
    setShowResults(false);
  }, [navigate]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!showResults || results.length === 0) {
      if (e.key === 'Enter' && query.trim().length >= 3) {
        navigate(`/reviewer/search?q=${encodeURIComponent(query.trim())}`);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((i) => (i < results.length - 1 ? i + 1 : i));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((i) => (i > 0 ? i - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]!);
        } else {
          navigate(`/reviewer/search?q=${encodeURIComponent(query.trim())}`);
        }
        break;
      case 'Escape':
        setShowResults(false);
        inputRef.current?.blur();
        break;
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', maxWidth: 560, margin: '0 auto 28px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: 8,
          padding: '0 14px',
          transition: 'border-color 0.2s',
        }}
      >
        <SearchOutlined style={{ color: '#666', fontSize: 16, flexShrink: 0 }} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setShowResults(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search essays and notes..."
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#f5f5f5',
            fontSize: '0.95rem',
            padding: '12px 10px',
            fontFamily: 'inherit',
          }}
        />
        {isSearching && <Spin size="small" />}
      </div>

      {/* Results dropdown */}
      {showResults && results.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: 8,
            overflow: 'hidden',
            zIndex: 50,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}`}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                background: index === selectedIndex ? '#2a2a2a' : 'transparent',
                borderBottom: index < results.length - 1 ? '1px solid #2a2a2a' : 'none',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <div style={{ color: '#d4af37', fontSize: 14, marginTop: 2, flexShrink: 0 }}>
                {result.type === 'essay' ? <BookOutlined /> : <FileTextOutlined />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#f5f5f5', fontSize: '0.9rem', fontWeight: 500, marginBottom: 2 }}>
                  {result.title}
                </div>
                <div style={{ color: '#888', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {result.preview}
                </div>
              </div>
              <div style={{ color: '#555', fontSize: '0.7rem', flexShrink: 0, marginTop: 2 }}>
                {Math.round(result.similarity * 100)}%
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showResults && results.length === 0 && !isSearching && debouncedQuery.length >= 3 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: 8,
            padding: '16px',
            zIndex: 50,
            textAlign: 'center',
            color: '#888',
            fontSize: '0.85rem',
          }}
        >
          No results found
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Stat & Action Cards
// =============================================================================

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: 8,
        padding: '12px 6px',
        textAlign: 'center',
      }}
    >
      <div style={{ color: '#d4af37', fontSize: 'clamp(0.9rem, 2.5vw, 1.2rem)', marginBottom: 4 }}>{icon}</div>
      <div style={{ color: '#f5f5f5', fontSize: 'clamp(1.1rem, 3vw, 1.6rem)', fontWeight: 600, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ color: '#a0a0a0', fontSize: 'clamp(0.6rem, 2vw, 0.8rem)', letterSpacing: '0.03em', marginTop: 3 }}>
        {label}
      </div>
    </div>
  );
}

function ActionCard({
  label,
  description,
  icon,
  onClick,
}: {
  label: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        backgroundColor: hover ? '#242424' : '#1a1a1a',
        border: hover ? '1px solid rgba(212, 175, 55, 0.5)' : '1px solid #333',
        borderRadius: 8,
        padding: '16px 8px',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.2s ease',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? '0 4px 16px rgba(212, 175, 55, 0.1)' : 'none',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 4,
      }}
    >
      <div style={{ color: '#d4af37', fontSize: '1.3rem' }}>{icon}</div>
      <div
        style={{
          color: '#f5f5f5',
          fontSize: 'clamp(0.75rem, 2.5vw, 0.95rem)',
          fontFamily: "var(--font-serif)",
          fontWeight: 600,
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </div>
      <div style={{ color: '#a0a0a0', fontSize: 'clamp(0.6rem, 2vw, 0.75rem)', lineHeight: 1.3 }}>{description}</div>
    </button>
  );
}

export default HomePage;
